import React, { useState } from 'react';
import { Card, Button, Alert, Modal, Row, Col, Badge, ProgressBar } from 'react-bootstrap';

const ATOSubmission = ({ form, onClose }) => {
  const [submissionStep, setSubmissionStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [authUrl, setAuthUrl] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingXML, setIsGeneratingXML] = useState(false);
  const [isGeneratingCSV, setIsGeneratingCSV] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPayloadModal, setShowPayloadModal] = useState(false);
  const [payloadData, setPayloadData] = useState(null);
  const [isLoadingPayload, setIsLoadingPayload] = useState(false);

  const steps = [
    { number: 1, title: 'Validation', description: 'Validate form data' },
    { number: 2, title: 'ATO Authentication', description: 'Connect to ATO' },
    { number: 3, title: 'Submit', description: 'Submit to ATO' },
    { number: 4, title: 'Complete', description: 'Submission complete' }
  ];

  const handleStartSubmission = async () => {
    setIsSubmitting(true);
    setSubmissionStep(1);

    try {
      // Step 1: Enhanced validation
      const validationResponse = await fetch('/api/validate-ato-enhanced', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const validation = await validationResponse.json();
      
      if (!validation.valid) {
        setSubmissionResult({
          success: false,
          errors: validation.errors,
          warnings: validation.warnings
        });
        setIsSubmitting(false);
        return;
      }

      // Step 2: Get ATO authorization URL
      setSubmissionStep(2);
      const authResponse = await fetch('/api/ato/auth-url');
      const authData = await authResponse.json();
      setAuthUrl(authData.url);

      // For demo purposes, simulate successful authentication
      setTimeout(() => {
        setSubmissionStep(3);
        handleATOSubmission();
      }, 2000);

    } catch (error) {
      setSubmissionResult({
        success: false,
        error: error.message
      });
      setIsSubmitting(false);
    }
  };

  const handleATOSubmission = async () => {
    try {
      // Step 3: Submit to ATO
      const response = await fetch(`/api/ato/submit-bas/${form.id}`, {
        method: 'POST'
      });

      const result = await response.json();
      
      setSubmissionStep(4);
      setSubmissionResult(result);
      setIsSubmitting(false);

    } catch (error) {
      setSubmissionResult({
        success: false,
        error: error.message
      });
      setIsSubmitting(false);
    }
  };

  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true);
    setError('');
    
    try {
      console.log('Generating PDF for form:', form);
      
      // First validate the form data
      if (!form.abn || !form.business_name) {
        throw new Error('Form data is incomplete. Please ensure ABN and business name are filled.');
      }
      
      const response = await fetch(`/api/ato/generate-pdf/${form.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('PDF generation response status:', response.status);
      
      if (response.ok) {
        // Check if response is actually a PDF
        const contentType = response.headers.get('content-type');
        console.log('Response content type:', contentType);
        
        if (contentType && contentType.includes('application/pdf')) {
          // Create a blob from the response
          const blob = await response.blob();
          console.log('PDF blob size:', blob.size);
          
          if (blob.size > 0) {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `BAS-${form.abn.replace(/\s/g, '')}-${form.period_start}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setSuccess('PDF generated and downloaded successfully!');
          } else {
            throw new Error('Generated PDF file is empty');
          }
        } else {
          // Try to parse as JSON error
          const errorData = await response.json();
          throw new Error(errorData.error || 'Invalid response format');
        }
      } else {
        const errorData = await response.json();
        console.error('PDF generation error:', errorData);
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      setError('Error generating PDF: ' + error.message);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportXML = async () => {
    setIsGeneratingXML(true);
    try {
      const response = await fetch(`/api/ato/export-xml/${form.id}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BAS-${form.abn}-${form.period_start}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('Error exporting XML: ' + error.message);
    } finally {
      setIsGeneratingXML(false);
    }
  };

  const handleExportCSV = async () => {
    setIsGeneratingCSV(true);
    try {
      const response = await fetch(`/api/ato/export-csv/${form.id}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BAS-${form.abn}-${form.period_start}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('Error exporting CSV: ' + error.message);
    } finally {
      setIsGeneratingCSV(false);
    }
  };

  const handlePreviewPayload = async () => {
    setIsLoadingPayload(true);
    setPayloadData(null);
    
    try {
      const response = await fetch(`/api/ato/preview-payload/${form.id}`);
      
      if (response.ok) {
        const data = await response.json();
        setPayloadData(data);
        setShowPayloadModal(true);
      } else {
        const errorData = await response.json();
        alert(`Error previewing payload: ${errorData.error}`);
      }
    } catch (error) {
      alert(`Error previewing payload: ${error.message}`);
    } finally {
      setIsLoadingPayload(false);
    }
  };

  const getStepStatus = (stepNumber) => {
    if (stepNumber < submissionStep) return 'completed';
    if (stepNumber === submissionStep) return 'active';
    return 'pending';
  };

  const getStepIcon = (stepNumber) => {
    const status = getStepStatus(stepNumber);
    if (status === 'completed') return 'fas fa-check-circle text-success';
    if (status === 'active') return 'fas fa-spinner fa-spin text-primary';
    return 'fas fa-circle text-muted';
  };

  return (
    <Modal show={true} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-paper-plane me-2"></i>
          ATO Submission - {form.business_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3">
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}
        {/* Progress Steps */}
        <div className="mb-4">
          <div className="d-flex justify-content-between">
            {steps.map((step) => (
              <div key={step.number} className="text-center">
                <div className={`mb-2 ${getStepStatus(step.number) === 'active' ? 'text-primary' : ''}`}>
                  <i className={`${getStepIcon(step.number)} fa-2x`}></i>
                </div>
                <div className="fw-bold">{step.title}</div>
                <div className="small text-muted">{step.description}</div>
              </div>
            ))}
          </div>
          <ProgressBar 
            now={(submissionStep / steps.length) * 100} 
            className="mt-3"
            style={{ height: '8px' }}
          />
        </div>

        {/* Form Summary */}
        <Card className="mb-4">
          <Card.Header>Form Summary</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <strong>Business:</strong> {form.business_name}<br />
                <strong>ABN:</strong> {form.abn}<br />
                <strong>Period:</strong> {form.period_start} to {form.period_end}
              </Col>
              <Col md={6}>
                <strong>Total Amount:</strong> ${form.total_amount}<br />
                <strong>Status:</strong> <Badge bg="secondary">{form.status}</Badge><br />
                <strong>Net GST:</strong> ${form.net_gst}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Submission Result */}
        {submissionResult && (
          <Alert variant={submissionResult.success ? 'success' : 'danger'}>
            <i className={`fas fa-${submissionResult.success ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
            <strong>
              {submissionResult.success ? 'Submission Successful!' : 'Submission Failed'}
            </strong>
            {submissionResult.success && submissionResult.submissionId && (
              <div className="mt-2">
                <strong>Submission ID:</strong> {submissionResult.submissionId}
              </div>
            )}
            {submissionResult.error && (
              <div className="mt-2">
                <strong>Error:</strong> {submissionResult.error}
              </div>
            )}
            {submissionResult.errors && submissionResult.errors.length > 0 && (
              <div className="mt-2">
                <strong>Errors:</strong>
                <ul className="mb-0">
                  {submissionResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </Alert>
        )}

        {/* Export Options */}
        <Card className="mb-4">
          <Card.Header>Export Options</Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Button
                  variant="outline-danger"
                  onClick={handleGeneratePDF}
                  disabled={isGeneratingPDF}
                  className="w-100 mb-2"
                >
                  {isGeneratingPDF ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-pdf me-1"></i>
                      Generate PDF
                    </>
                  )}
                </Button>
              </Col>
              <Col md={3}>
                <Button
                  variant="outline-info"
                  onClick={handleExportXML}
                  disabled={isGeneratingXML}
                  className="w-100 mb-2"
                >
                  {isGeneratingXML ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-code me-1"></i>
                      Export XML
                    </>
                  )}
                </Button>
              </Col>
              <Col md={3}>
                <Button
                  variant="outline-success"
                  onClick={handleExportCSV}
                  disabled={isGeneratingCSV}
                  className="w-100 mb-2"
                >
                  {isGeneratingCSV ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-csv me-1"></i>
                      Export CSV
                    </>
                  )}
                </Button>
              </Col>
              <Col md={3}>
                <Button
                  variant="outline-warning"
                  onClick={handlePreviewPayload}
                  disabled={isLoadingPayload}
                  className="w-100 mb-2"
                >
                  {isLoadingPayload ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-eye me-1"></i>
                      Preview Payload
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ATO Authentication URL */}
        {authUrl && submissionStep === 2 && (
          <Alert variant="info">
            <i className="fas fa-info-circle me-2"></i>
            <strong>ATO Authentication Required</strong>
            <div className="mt-2">
              <a href={authUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                <i className="fas fa-external-link-alt me-1"></i>
                Open ATO Portal
              </a>
            </div>
          </Alert>
        )}

        {/* ATO Payload Preview Modal */}
        <Modal show={showPayloadModal} onHide={() => setShowPayloadModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-eye me-2"></i>
              ATO API Payload Preview
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {payloadData && (
              <div>
                {/* ATO API Summary */}
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">
                      ATO API Submission Summary
                      <Badge 
                        bg={payloadData.mode === 'LIVE' ? "success" : "secondary"} 
                        className="ms-2"
                      >
                        {payloadData.mode || 'SIMULATED'}
                      </Badge>
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={4}>
                        <strong>Message ID:</strong><br />
                        <span className="text-primary small">{payloadData.messageId}</span>
                      </Col>
                      <Col md={4}>
                        <strong>Business ABN:</strong><br />
                        <span className="text-info">{payloadData.businessDetails?.abn}</span>
                      </Col>
                      <Col md={4}>
                        <strong>Form Type:</strong><br />
                        <span className="text-success">{payloadData.formDetails?.formType} {payloadData.formDetails?.formVersion}</span>
                      </Col>
                    </Row>
                    <Row className="mt-3">
                      <Col md={3}>
                        <strong>GST on Sales:</strong><br />
                        <span className="text-success fs-5">${payloadData.gstDetails?.gstOnSales?.amount?.toFixed(2) || '0.00'}</span>
                      </Col>
                      <Col md={3}>
                        <strong>GST on Purchases:</strong><br />
                        <span className="text-info fs-5">${payloadData.gstDetails?.gstOnPurchases?.amount?.toFixed(2) || '0.00'}</span>
                      </Col>
                      <Col md={3}>
                        <strong>PAYG Withholding:</strong><br />
                        <span className="text-warning fs-5">${payloadData.paygDetails?.paygWithholding?.toFixed(2) || '0.00'}</span>
                      </Col>
                      <Col md={3}>
                        <strong>Total Payable:</strong><br />
                        <span className="text-danger fs-5">${payloadData.summary?.totalAmountPayable?.toFixed(2) || '0.00'}</span>
                      </Col>
                    </Row>
                    <Row className="mt-3">
                      <Col md={6}>
                        <strong>Period:</strong> {payloadData.formDetails?.periodStart} to {payloadData.formDetails?.periodEnd}
                      </Col>
                      <Col md={6}>
                        <strong>Due Date:</strong> {payloadData.formDetails?.dueDate}
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col md={12}>
                        <strong>Validation Status:</strong>
                        <div className="mt-1">
                          <Badge bg={payloadData.validation?.abnValid ? "success" : "danger"} className="me-2">
                            ABN Valid: {payloadData.validation?.abnValid ? "Yes" : "No"}
                          </Badge>
                          <Badge bg={payloadData.validation?.amountsBalanced ? "success" : "danger"} className="me-2">
                            Amounts Balanced: {payloadData.validation?.amountsBalanced ? "Yes" : "No"}
                          </Badge>
                          <Badge bg={payloadData.validation?.readyForSubmission ? "success" : "warning"}>
                            Ready for Submission: {payloadData.validation?.readyForSubmission ? "Yes" : "No"}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Raw ATO API JSON Payload */}
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Raw ATO API JSON Payload</h6>
                  </Card.Header>
                  <Card.Body>
                    <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
                      {JSON.stringify(payloadData, null, 2)}
                    </pre>
                  </Card.Body>
                </Card>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPayloadModal(false)}>
              Close
            </Button>
            <Button variant="info" onClick={() => {
              if (payloadData) {
                const blob = new Blob([JSON.stringify(payloadData, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `ATO-Payload-${form.abn}-${form.period_start}.json`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              }
            }}>
              <i className="fas fa-download me-1"></i>
              Download JSON
            </Button>
          </Modal.Footer>
        </Modal>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {submissionStep === 1 && !isSubmitting && (
          <Button variant="primary" onClick={handleStartSubmission}>
            <i className="fas fa-paper-plane me-1"></i>
            Start ATO Submission
          </Button>
        )}
        {isSubmitting && (
          <Button variant="primary" disabled>
            <i className="fas fa-spinner fa-spin me-1"></i>
            Processing...
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ATOSubmission;
